import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { fetchCurrentUser, type CurrentUser } from "../api/client";
import { normalizePathname } from "../utils/anchor";
import { useEpisod } from "./useEpisod";

const APP_NAME = "MCC";

/** URL search params that describe what the user is analysing. */
const TRACKED_PARAMS = ["anchor", "countries", "sub_markets", "sales_channels", "period", "frame_types"] as const;

const PAGE_LABELS: Record<string, string> = {
    "/": "Home",
    "/top": "Top Combinations",
    "/analysis": "Analysis",
    "/features": "Features",
};

function pageLabel(pathname: string): string {
    return PAGE_LABELS[normalizePathname(pathname)] ?? pathname;
}

function filterCustom(search: string): Record<string, unknown> {
    const params = new URLSearchParams(search);
    const custom: Record<string, unknown> = {};
    for (const key of TRACKED_PARAMS) {
        const value = params.get(key);
        if (value) custom[key] = value;
    }
    return custom;
}

/**
 * Wires Episod usage tracking into the app. Call once from a component that
 * stays mounted across navigations (e.g. Layout). Initialises the client,
 * loads the user's role/location context, and sends a page view on every
 * route change. Events on anchor pages (/, /top, /analysis) carry the current
 * anchor in `custom.anchor`; other pages carry none.
 */
export function useAnalytics(): void {
    const { initEpisod, setCommons, sendPageView } = useEpisod();
    const location = useLocation();
    const userRef = useRef<CurrentUser | null>(null);
    const commonsSet = useRef(false);

    // Initialise once and load the user context.
    useEffect(() => {
        initEpisod();
        let cancelled = false;
        void fetchCurrentUser().then((user) => {
            if (cancelled || !user) return;
            userRef.current = user;
            setCommons({
                appName: APP_NAME,
                language: typeof navigator !== "undefined" ? navigator.language : "en",
                sub: user.sub,
                country: user.country,
                department: user.department,
                jobTitle: user.job_title,
                locationId: user.location,
                officeLocation: user.office_location,
            });
            commonsSet.current = true;
            // Emit the first page view now that user context is attached.
            sendPageView(
                location.pathname + location.search,
                pageLabel(location.pathname),
                filterCustom(location.search)
            );
        });
        return () => {
            cancelled = true;
        };
    }, []);

    // Send a page view whenever the path changes (after the initial load).
    useEffect(() => {
        if (!commonsSet.current) return;
        sendPageView(location.pathname + location.search, pageLabel(location.pathname), filterCustom(location.search));
        // Only re-send on pathname changes, not on every query-param tweak.
    }, [location.pathname]);
}
